import { ensureThemeJsonArray } from "../../functions/themeAssets";

export const CORNERS = ["tl", "tr", "bl", "br"];

export function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function parseThemeBool(val, fallback = false) {
  if (val === undefined || val === null || val === "") return fallback;
  if (typeof val === "boolean") return val;
  const s = String(val).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

export function formatMmSs(totalSeconds) {
  const s = Math.max(0, Number(totalSeconds) || 0);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

export function resolveThemeBackdropKey(themeData, preferMobile) {
  if (!themeData) return "";
  const desk = themeData.background_desk ?? themeData.desktop_view_image;
  const mob = themeData.background_mob ?? themeData.mobile_view_image;
  const ds = desk != null ? String(desk).trim() : "";
  const ms = mob != null ? String(mob).trim() : "";
  if (preferMobile && ms) return ms;
  if (!preferMobile && ds) return ds;
  return ds || ms;
}

/** Map heart_items from theme into playable rows */
export function parseHeartItems(themeData) {
  const raw = ensureThemeJsonArray(themeData?.heart_items);
  return raw
    .map((it, i) => ({
      id: `${it.isGood ? "g" : "b"}-${it.sortOrder ?? i}-${String(it.name ?? "").trim()}`,
      name: String(it.name ?? "").trim(),
      type: String(it.type ?? "text"),
      contentFilename: String(it.contentFilename ?? "").trim(),
      description: String(it.description ?? "").trim(),
      mascotType: String(it.mascotType ?? "gif"),
      mascotText: String(it.mascotText ?? "").trim(),
      mascotFilename: String(it.mascotFilename ?? "").trim(),
      isGood: Boolean(it.isGood),
      sortOrder: Number(it.sortOrder ?? i + 1) || i + 1,
    }))
    .filter((it) => it.name);
}

export function idleTierForScore(score) {
  const s = Number(score) || 0;
  if (s >= 65) return "high";
  if (s >= 25) return "mid";
  return "low";
}

/** Same 5-bucket scale admin uses for `feedback_emojis` / `leaderboard_tiers` scoreKeys. */
export function scoreKeyForScore(score) {
  const s = Number(score) || 0;
  if (s >= 65) return "65_plus";
  if (s >= 45) return "45_60";
  if (s >= 25) return "25_40";
  if (s >= 5) return "5_20";
  return "le_0";
}

export function idleVideoKey(tier) {
  if (tier === "high") return "idle_heart_high";
  if (tier === "low") return "idle_heart_low";
  return "idle_heart_mid";
}

/** Assign 2 good + 2 bad into four corners (shuffled) */
export function buildInitialSlots(goodQueue, badQueue) {
  const corners = shuffleArray(CORNERS);
  const items = shuffleArray([
    goodQueue.shift(),
    goodQueue.shift(),
    badQueue.shift(),
    badQueue.shift(),
  ]);
  return corners.map((corner, i) => ({ corner, item: items[i] ?? null }));
}

/**
 * Refill the board after a drop: replace the picked item (keeping the 2 good / 2 bad
 * balance whenever both queues still have stock — falling back to whichever queue
 * still has items if one has run dry, so a slot is never left empty while items
 * remain) and re-shuffle ALL four corners, not just the vacated one, so which corner
 * is "the healthy one" changes round to round instead of staying fixed all game.
 */
export function refillBoard(prevSlots, removedCorner, goodQueue, badQueue) {
  const removedItem = prevSlots.find((s) => s.corner === removedCorner)?.item;
  const remainingItems = prevSlots
    .filter((s) => s.corner !== removedCorner)
    .map((s) => s.item)
    .filter(Boolean);

  const wantGood = removedItem?.isGood;
  let next = wantGood ? goodQueue.shift() : badQueue.shift();
  if (!next) {
    // Preferred type ran out — fall back to whichever queue still has stock
    // rather than leaving the slot empty.
    next = badQueue.shift() ?? goodQueue.shift() ?? null;
  }

  const items = next ? [...remainingItems, next] : remainingItems;
  const corners = shuffleArray(CORNERS);
  const shuffledItems = shuffleArray(items);
  return corners.map((corner, i) => ({ corner, item: shuffledItems[i] ?? null }));
}

export function countGoodOnBoard(slots) {
  return slots.filter((s) => s.item?.isGood).length;
}

export function allGoodItemsDone(goodQueue, slots) {
  return goodQueue.length === 0 && countGoodOnBoard(slots) === 0;
}

export function pointInRect(x, y, rect) {
  if (!rect) return false;
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

/** PNG/GIF/WebP mascots — use <img> so transparency is preserved (video tag shows black). */
export function isImageMediaUrl(url) {
  return /\.(png|gif|webp|jpe?g)(\?|$)/i.test(String(url || ""));
}
